// File: client/src/components/rules/expressionEditor/SimpleConditionalEditor.js
import React, { useState, useEffect, useCallback } from 'react';
import { Space, Button, Typography, Row, Col } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSampleData } from './SampleDataContext';
import SampleDataViewer from './SampleDataViewer';
import ConditionRow from './ConditionRow';
import GroupRow from './GroupRow';
import { OPERATORS, LOGIC_OPERATORS } from './constants';

const { Text } = Typography;

const SimpleConditionalEditor = ({ value, onChange, evaluateOnChange = false }) => {
    const { currentSample } = useSampleData();
    const [availableFields, setAvailableFields] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [nextId, setNextId] = useState(1);
    const [evaluationResult, setEvaluationResult] = useState(null);
    const [evaluationError, setEvaluationError] = useState(null);

    // State for displaying generated code (hidden debug feature)
    const [showGeneratedExpression, setShowGeneratedExpression] = useState(false);

    // Toggle showing generated expression
    const toggleShowGeneratedExpression = () => {
        setShowGeneratedExpression(prev => !prev);
    };

    // Track multiple selected nodes
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);

    // Generate a unique ID for new nodes
    const generateId = useCallback(() => {
        const id = nextId;
        setNextId(prev => prev + 1);
        return id;
    }, [nextId]);

    // Find a node by ID (even in nested groups)
    const findNodeById = useCallback((id) => {
        // Helper function to recursively search for the node
        const findNodeInTree = (nodeArray) => {
            for (const node of nodeArray) {
                if (node.id === id) {
                    return node;
                }

                if (node.type === 'group' && node.conditions) {
                    const foundInGroup = findNodeInTree(node.conditions);
                    if (foundInGroup) {
                        return foundInGroup;
                    }
                }
            }

            return null;
        };

        return findNodeInTree(nodes);
    }, [nodes]);

    // Find the first condition in the tree (even in nested groups)
    const findFirstCondition = useCallback(() => {
        // Helper function to recursively search for the first condition
        const findConditionInTree = (nodeArray) => {
            for (const node of nodeArray) {
                if (node.type === 'condition') {
                    return node;
                }

                if (node.type === 'group' && node.conditions) {
                    const foundInGroup = findConditionInTree(node.conditions);
                    if (foundInGroup) {
                        return foundInGroup;
                    }
                }
            }

            return null;
        };

        return findConditionInTree(nodes);
    }, [nodes]);

    // Helper to extract fields from a sample data record recursively
    const extractFields = useCallback((obj, prefix = '') => {
        if (!obj || typeof obj !== 'object') return [];

        let fields = [];
        Object.entries(obj).forEach(([key, value]) => {
            const fieldPath = prefix ? `${prefix}.${key}` : key;
            fields.push({
                path: fieldPath,
                type: Array.isArray(value) ? 'array' : typeof value,
                value
            });

            // Recursively add nested fields
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                fields = [...fields, ...extractFields(value, fieldPath)];
            }
        });

        return fields;
    }, []);

    // Check if a node can be grouped with currently selected nodes
    const canNodeBeGrouped = (id) => {
        // Any node can always be selected
        return true;
    };

    // Create a default condition
    const createDefaultCondition = useCallback(() => {
        const newCondition = {
            id: generateId(),
            type: 'condition',
            field: '',
            operator: 'equals',
            value: ''
        };

        setNodes([newCondition]);
        return newCondition;
    }, [generateId]);

    // Initialize with sample data fields
    useEffect(() => {
        if (currentSample) {
            const fields = extractFields(currentSample).map(field => ({
                ...field,
                path: `record.${field.path}`
            }));
            setAvailableFields(fields);

            // If no nodes exist yet, create a default condition
            if (nodes.length === 0) {
                createDefaultCondition();
            }
        }
    }, [currentSample, extractFields, nodes.length, createDefaultCondition]);

    // Add a new condition
    const addCondition = () => {
        const newCondition = {
            id: generateId(),
            type: 'condition',
            field: '',
            operator: 'equals',
            value: ''
        };

        setNodes(prev => [...prev, newCondition]);

        // Evaluate after adding
        setTimeout(() => evaluateExpression(), 100);

        return newCondition;
    };

    // Handle node selection (exclusive=true will make this the ONLY selected node)
    const handleNodeSelect = (id, exclusive = false) => {
        // If this is a selection triggered by focusing on a control, make it exclusive
        if (exclusive) {
            setSelectedNodeIds([id]);
            return;
        }

        // If clicking an already selected node, deselect it
        if (selectedNodeIds.includes(id)) {
            setSelectedNodeIds(prev => prev.filter(nodeId => nodeId !== id));
            return;
        }

        // Function to get the top-level parent groups of each node
        const getTopLevelParent = (nodeId) => {
            // Helper to find all parent groups
            const findParentPath = (id, nodeArray, parentIds = []) => {
                for (const node of nodeArray) {
                    if (node.type === 'group' && node.conditions) {
                        // Check if this node is a direct child of the current group
                        if (node.conditions.some(c => c.id === id)) {
                            return [...parentIds, node.id];
                        }

                        // Try to find in this group's children
                        const foundInChild = findParentPath(
                            id,
                            node.conditions.filter(c => c.type === 'group'),
                            [...parentIds, node.id]
                        );
                        if (foundInChild) return foundInChild;
                    }
                }
                return parentIds; // Node is at top level (no parents)
            };

            return findParentPath(nodeId, nodes);
        };

        // Get parent paths for all currently selected nodes
        const selectedParentPaths = selectedNodeIds.map(getTopLevelParent);

        // If all selected nodes have the same parent path, we can add this one
        // if it has the same parent path
        const newNodeParentPath = getTopLevelParent(id);

        // Check if all parent paths are the same and the new node's parent path matches
        const allSameParent = selectedParentPaths.length === 0 ||
            selectedParentPaths.every(
                path => JSON.stringify(path) === JSON.stringify(selectedParentPaths[0])
            );

        if (allSameParent &&
            (selectedParentPaths.length === 0 ||
                JSON.stringify(newNodeParentPath) === JSON.stringify(selectedParentPaths[0]))) {
            // Add to selection
            setSelectedNodeIds(prev => [...prev, id]);
        } else {
            // Replace selection if parent paths don't match
            setSelectedNodeIds([id]);
        }
    };

    // Add a condition to a group
    const addConditionToGroup = (groupId) => {
        const newCondition = {
            id: generateId(),
            type: 'condition',
            field: '',
            operator: 'equals',
            value: ''
        };

        setNodes(prev => {
            return prev.map(node => {
                if (node.id === groupId && node.type === 'group') {
                    return {
                        ...node,
                        conditions: [...node.conditions, newCondition]
                    };
                }
                return node;
            });
        });

        // Select only the new condition
        setSelectedNodeIds([newCondition.id]);

        // Evaluate after adding
        setTimeout(() => evaluateExpression(), 100);

        return newCondition;
    };

    // Ungroup a group, keeping its conditions
    const ungroupConditions = (groupId) => {
        const group = findNodeById(groupId);
        if (!group || group.type !== 'group') return;

        // Replace the group with its conditions
        setNodes(prev => [
            ...prev.filter(n => n.id !== groupId),
            ...group.conditions
        ]);

        // Clear selections
        setSelectedNodeIds([]);

        // Evaluate after ungrouping
        setTimeout(() => evaluateExpression(), 100);
    };

    // Create a group from selected conditions
    const createGroup = (operator = 'and') => {
        if (selectedNodeIds.length < 2) return;

        // Get all selected nodes
        const selectedNodes = selectedNodeIds.map(id => findNodeById(id)).filter(Boolean);
        if (selectedNodes.length < 2) return;

        // Get parent IDs for all selected nodes
        const getNodeParentId = (nodeId) => {
            for (const node of nodes) {
                if (node.type === 'group' && node.conditions) {
                    if (node.conditions.some(c => c.id === nodeId)) {
                        return node.id;
                    }

                    // Check recursively in nested groups
                    for (const childGroup of node.conditions.filter(c => c.type === 'group')) {
                        const foundInChild = getNodeParentId(nodeId, childGroup.conditions);
                        if (foundInChild) return foundInChild;
                    }
                }
            }
            return null; // No parent found (top-level node)
        };

        const parentIds = selectedNodeIds.map(getNodeParentId);

        // Check if all selected nodes have the same parent (or all are top-level)
        const allSameParent = parentIds.every(id => id === parentIds[0]);
        if (!allSameParent) {
            console.warn('Cannot group nodes with different parents');
            return;
        }

        // Create a new group containing all selected nodes
        const newGroup = {
            id: generateId(),
            type: 'group',
            operator,
            conditions: selectedNodes
        };

        if (parentIds[0] === null) {
            // All nodes are top-level - remove original nodes and add the new group at top level
            setNodes(prev => [
                ...prev.filter(n => !selectedNodeIds.includes(n.id)),
                newGroup
            ]);
        } else {
            // All nodes have the same parent - update that parent group
            setNodes(prev =>
                prev.map(node => {
                    if (node.id === parentIds[0]) {
                        // This is the parent - replace selected conditions with the new group
                        return {
                            ...node,
                            conditions: [
                                ...node.conditions.filter(c => !selectedNodeIds.includes(c.id)),
                                newGroup
                            ]
                        };
                    }
                    return node;
                })
            );
        }

        // Reset selection to the new group
        setSelectedNodeIds([newGroup.id]);

        // Evaluate after grouping
        setTimeout(() => evaluateExpression(), 100);
    };

    // Update a node
    const updateNode = (updatedNode) => {
        // Helper function to update a node within a group
        const updateNodeInGroup = (group, nodeId, updatedNode) => {
            // Check if the node is a direct child of this group
            const directChildIndex = group.conditions.findIndex(c => c.id === nodeId);
            if (directChildIndex !== -1) {
                // Node is a direct child, update it
                const newConditions = [...group.conditions];
                newConditions[directChildIndex] = updatedNode;
                return {
                    ...group,
                    conditions: newConditions
                };
            }

            // Check if the node is in a nested group
            const newConditions = group.conditions.map(condition => {
                if (condition.type === 'group') {
                    return updateNodeInGroup(condition, nodeId, updatedNode);
                }
                return condition;
            });

            return {
                ...group,
                conditions: newConditions
            };
        };

        setNodes(prev =>
            prev.map(node => {
                if (node.id === updatedNode.id) {
                    return updatedNode;
                } else if (node.type === 'group') {
                    return updateNodeInGroup(node, updatedNode.id, updatedNode);
                }
                return node;
            })
        );

        // Always trigger evaluation after any update
        setTimeout(() => evaluateExpression(), 100);
    };

    // Delete a node
    const deleteNode = (id) => {
        // Helper function to delete a node from a group
        const deleteNodeFromGroup = (group, nodeId) => {
            // Check if the node is a direct child of this group
            const directChildIndex = group.conditions.findIndex(c => c.id === nodeId);
            if (directChildIndex !== -1) {
                // Node is a direct child, remove it
                const newConditions = group.conditions.filter(c => c.id !== nodeId);
                return {
                    ...group,
                    conditions: newConditions
                };
            }

            // Check if the node is in a nested group
            const newConditions = group.conditions.map(condition => {
                if (condition.type === 'group') {
                    return deleteNodeFromGroup(condition, nodeId);
                }
                return condition;
            });

            return {
                ...group,
                conditions: newConditions
            };
        };

        setNodes(prev =>
            prev.filter(node => {
                if (node.id === id) {
                    return false;
                } else if (node.type === 'group') {
                    return true; // Keep the group, but process its children
                }
                return true;
            }).map(node => {
                if (node.type === 'group') {
                    return deleteNodeFromGroup(node, id);
                }
                return node;
            })
        );

        // Remove the deleted node from selection
        setSelectedNodeIds(prev => prev.filter(nodeId => nodeId !== id));

        // Evaluate after deleting
        setTimeout(() => evaluateExpression(), 100);
    };

    // Generate JavaScript code from the nodes
    const generateJavaScriptExpression = useCallback(() => {
        if (nodes.length === 0) return '';

        const nodeToExpression = (node) => {
            if (node.type === 'condition') {
                const field = node.field;
                if (!field) return '';

                const op = OPERATORS[node.operator].code;
                const value = node.value;

                // Check if string comparison
                const fieldObj = availableFields.find(f => f.path === field);
                const isString = fieldObj && fieldObj.type === 'string';

                // Always format string values for string methods (contains, startsWith, endsWith)
                if (['contains', 'startsWith', 'endsWith'].includes(node.operator)) {
                    // Always quote the value for these string methods unless it's already quoted
                    let formattedValue = value;
                    if (!value.startsWith('"') && !value.startsWith("'")) {
                        formattedValue = `"${value}"`;
                    }
                    return `${field}${op}(${formattedValue})`;
                }

                // Handle regex matching
                if (node.operator === 'matchesRegex') {
                    // Don't add quotes for regex patterns
                    let regexPattern = value;
                    if (regexPattern.startsWith('/') && (regexPattern.endsWith('/') ||
                        regexPattern.endsWith('/g') || regexPattern.endsWith('/i') ||
                        regexPattern.endsWith('/m'))) {
                        // Already a properly formatted regex
                        return `${field}${op}(${regexPattern})`;
                    } else {
                        // Convert to a regex object
                        return `${field}${op}(new RegExp("${value.replace(/"/g, '\\"')}"))`;
                    }
                }

                // Format value properly for regular operators
                let formattedValue = value;
                if (isString && !value.startsWith('"') && !value.startsWith("'")) {
                    formattedValue = `"${value}"`;
                }

                return `${field} ${op} ${formattedValue}`;
            } else if (node.type === 'group') {
                if (node.conditions.length === 0) return '';
                if (node.conditions.length === 1) return nodeToExpression(node.conditions[0]);

                const conditionExpressions = node.conditions.map(nodeToExpression);
                const logicOp = LOGIC_OPERATORS[node.operator].code;

                return `(${conditionExpressions.join(` ${logicOp} `)})`;
            }

            return '';
        };

        if (nodes.length === 1) {
            return nodeToExpression(nodes[0]);
        }

        // Default to AND for multiple top-level nodes
        return nodes.map(nodeToExpression).filter(Boolean).join(' && ');
    }, [nodes, availableFields]);

    // Evaluate the expression
    const evaluateExpression = useCallback(() => {
        if (!currentSample) {
            setEvaluationError("No sample data available");
            setEvaluationResult(null);
            return;
        }

        const jsExpression = generateJavaScriptExpression();
        if (!jsExpression) {
            setEvaluationError(null);
            setEvaluationResult(null);
            return;
        }

        try {
            // Build and evaluate a function with the expression
            const record = currentSample;
            const workflow = { id: 'test-workflow' };

            const funcBody = `return ${jsExpression};`;
            const evalFunction = new Function('record', 'workflow', funcBody);

            const result = evalFunction(record, workflow);

            setEvaluationResult(result);
            setEvaluationError(null);

            // Notify parent of changes
            if (onChange) {
                onChange(jsExpression);
            }
        } catch (error) {
            console.error("Evaluation error:", error);
            setEvaluationResult(null);
            setEvaluationError(error.message || "Invalid expression");
        }
    }, [currentSample, generateJavaScriptExpression, onChange]);

    // Sync with external value if provided
    useEffect(() => {
        if (value && typeof value === 'string' && nodes.length === 0) {
            // TODO: In a real implementation, you might want to parse the JS expression
            // back into nodes, but that's complex and beyond this example
            createDefaultCondition();
        }
    }, [value, nodes.length, createDefaultCondition]);

    // Display generated expression and evaluation results
    const renderEvaluationResult = () => {
        const jsExpression = generateJavaScriptExpression();

        return (
            <div
                style={{ marginTop: 8, cursor: 'pointer' }}
                onClick={toggleShowGeneratedExpression}
            >
                {showGeneratedExpression && (
                    <div style={{
                        marginBottom: 8,
                        padding: '8px',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #d9d9d9',
                        borderRadius: '2px',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                    }}>
                        {jsExpression || '<empty expression>'}
                    </div>
                )}

                {evaluationError ? (
                    <div style={{ color: '#ff4d4f' }}>
                        <Text type="danger">Error: {evaluationError}</Text>
                    </div>
                ) : evaluationResult !== undefined && evaluationResult !== null ? (
                    <div>
                        <Text type="success">
                            Result: <Text code>{String(evaluationResult)}</Text>
                            <Text type="secondary"> ({typeof evaluationResult})</Text>
                        </Text>
                    </div>
                ) : null}
            </div>
        );
    };

    // Render bottom action buttons
    const renderBottomActionButtons = () => {
        const canCombine = selectedNodeIds.length >= 2;

        return (
            <Row justify="end" gutter={8} style={{ marginTop: 8 }}>
                {canCombine && (
                    <>
                        <Col>
                            <Button
                                size="small"
                                onClick={() => createGroup('and')}
                                style={{ backgroundColor: '#e6f7ff', borderColor: '#1890ff' }}
                            >
                                AND
                            </Button>
                        </Col>

                        <Col>
                            <Button
                                size="small"
                                onClick={() => createGroup('or')}
                                style={{ backgroundColor: '#fff7e6', borderColor: '#ffa940' }}
                            >
                                OR
                            </Button>
                        </Col>
                    </>
                )}
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            const newCondition = addCondition();
                            // Select only the new condition
                            setSelectedNodeIds([newCondition.id]);
                        }}
                        size="small"
                    >
                        Add
                    </Button>
                </Col>
            </Row>
        );
    };

    return (
        <div className="simple-conditional-editor">
            <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ minHeight: '100px' }}>
                    {nodes.map(node => (
                        node.type === 'condition' ? (
                            <ConditionRow
                                key={node.id}
                                condition={node}
                                onUpdate={updateNode}
                                onDelete={deleteNode}
                                availableFields={availableFields}
                                onHighlight={handleNodeSelect}
                                isHighlighted={selectedNodeIds.includes(node.id)}
                            />
                        ) : (
                            <GroupRow
                                key={node.id}
                                group={node}
                                onUpdate={updateNode}
                                onDelete={deleteNode}
                                onAddCondition={addConditionToGroup}
                                onUngroup={ungroupConditions}
                                availableFields={availableFields}
                                selectedNodeIds={selectedNodeIds}
                                onHighlight={handleNodeSelect}
                                isHighlighted={selectedNodeIds.includes(node.id)}
                            />
                        )
                    ))}
                </div>

                {renderBottomActionButtons()}

                {renderEvaluationResult()}

                <SampleDataViewer
                    onInsertText={(text, isPropertyName = false) => {
                        // If we have a selected node, use the first one
                        if (selectedNodeIds.length > 0) {
                            const node = findNodeById(selectedNodeIds[0]);

                            if (node && node.type === 'condition') {
                                if (isPropertyName || text.startsWith('record.')) {
                                    // For property names or record paths, update the field
                                    updateNode({
                                        ...node,
                                        field: text
                                    });
                                } else {
                                    // For values, remove quotes if present (they'll be added by the expression generator if needed)
                                    let cleanValue = text;
                                    if (typeof cleanValue === 'string') {
                                        if ((cleanValue.startsWith('"') && cleanValue.endsWith('"')) ||
                                            (cleanValue.startsWith("'") && cleanValue.endsWith("'"))) {
                                            cleanValue = cleanValue.substring(1, cleanValue.length - 1);
                                        }
                                    }

                                    updateNode({
                                        ...node,
                                        value: cleanValue
                                    });
                                }
                            }
                        } else if (nodes.length > 0) {
                            // If no node is selected, use the first condition
                            const firstCondition = findFirstCondition();
                            if (firstCondition) {
                                // First select this node
                                setSelectedNodeIds([firstCondition.id]);

                                // Then update it based on the text type
                                if (isPropertyName || text.startsWith('record.')) {
                                    updateNode({
                                        ...firstCondition,
                                        field: text
                                    });
                                } else {
                                    // Remove quotes from values
                                    let cleanValue = text;
                                    if (typeof cleanValue === 'string') {
                                        if ((cleanValue.startsWith('"') && cleanValue.endsWith('"')) ||
                                            (cleanValue.startsWith("'") && cleanValue.endsWith("'"))) {
                                            cleanValue = cleanValue.substring(1, cleanValue.length - 1);
                                        }
                                    }

                                    updateNode({
                                        ...firstCondition,
                                        value: cleanValue
                                    });
                                }
                            }
                        }
                    }}
                    onExecuteTest={evaluateExpression}
                />
            </Space>
        </div>
    );
};

export default SimpleConditionalEditor;
